import type { Issue, IssueSize, IssueStatus } from './types';
import { startGate, type DepCard } from './dependencies';
import { workingDaysBetween, DEFAULT_CALENDAR, type WorkCalendar } from './work-calendar';

/**
 * HAS THIS CARD GONE QUIET? — the idle-nudge signal.
 *
 * A card that sits for days with nobody moving it is the thing a board loses work
 * to: not the card that is hard, the card that is forgotten. This module judges
 * whether a card has gone idle and, if so, SINCE WHEN — so a later surface can
 * nudge the person doing it ("nothing has moved here in a week").
 *
 * WHY A WHOLE MODULE AND NOT A ONE-LINE `now - updatedAt > N`. Each of the three
 * things that one-liner gets wrong is a decision this module makes on purpose:
 *
 *   · A BIG CARD IS ALLOWED TO BE QUIET LONGER. A week on a long job is a Tuesday;
 *     a week on a quick one is a card somebody dropped. The window is scaled by the
 *     card's size, so the signal does not cry wolf on work that legitimately takes
 *     time. The windows are board-CONFIGURABLE — "too long" is a team's own
 *     judgement, not ours to hard-code — so they arrive as an argument, never a
 *     constant baked in here.
 *   · WEEKENDS ARE NOT IDLE TIME. Nobody worked the card on Saturday, so counting
 *     Saturday against it is counting hours nobody was ever going to spend. Elapsed
 *     time is measured in WORKING days (Mon–Fri).
 *   · SOME QUIET IS NOT A DROPPED BALL. A finished card, a card deliberately on
 *     hold, and a card legitimately waiting on an unfinished predecessor are all
 *     quiet for good reasons, and nudging their doer is the false alarm that trains
 *     a team to ignore the signal. Those cards are exempt (see `idleVerdict`).
 *
 * PURE ON PURPOSE. No store, no React, no clock of its own — the caller passes the
 * card, the rest of the board (to resolve dependencies), the windows and `nowMs`.
 * That is what lets the rule be proved against a fixed "today" in
 * scripts/test-idle-nudge.ts rather than argued about.
 */

/**
 * HOW LONG A CARD MAY SIT BEFORE IT IS CALLED IDLE, in WORKING days, by size.
 *
 * `bySize` is keyed by the short size letters (s / m / l), not the stored words,
 * so the board's settings row and this config read the same three keys. `noSize`
 * is the fallback for a card nobody has sized — most boards have some, and leaving
 * them un-nudgeable would quietly exempt exactly the cards most likely to have
 * been forgotten.
 */
export interface IdleWindows {
  bySize: Record<'s' | 'm' | 'l', number>;
  noSize: number;
}

/**
 * THE WINDOWS A BOARD STARTS WITH, until it says otherwise.
 *
 * 3 / 5 / 10 working days for quick / medium / long: a quick card quiet for most
 * of a working week is stuck; a long one is given two full weeks before the same
 * is said of it. `noSize` FALLS BACK TO THE MEDIUM DEFAULT (5) deliberately — an
 * unsized card is treated as the middle case rather than the most or least
 * forgiving, so sizing a card can only ever tighten or loosen the window from a
 * sensible centre, never flip it to an extreme. These are only the STARTING
 * values; a board passes its own `IdleWindows` to override them.
 */
export const DEFAULT_IDLE_WINDOWS: IdleWindows = {
  bySize: { s: 3, m: 5, l: 10 },
  noSize: 5,
};

/** The stored size words map to the short keys the windows are configured by. */
const SIZE_KEY: Record<IssueSize, 's' | 'm' | 'l'> = {
  quick: 's',
  medium: 'm',
  long: 'l',
};

/**
 * THE WINDOW FOR ONE CARD'S SIZE. A sized card reads its size's window; a card
 * with no size ('' or undefined — the two ways the field says "unset") falls back
 * to `windows.noSize`. One place, so no caller re-derives the fallback and gets it
 * subtly different.
 */
export function windowForSize(
  size: IssueSize | '' | undefined,
  windows: IdleWindows,
): number {
  if (!size) return windows.noSize;
  return windows.bySize[SIZE_KEY[size]];
}

/**
 * THE LANES WHOSE QUIET IS WORTH NUDGING — the active work a doer is expected to
 * be moving. todo through inreview, plus staging: a card somewhere on the board
 * where somebody owns the next move.
 *
 * WHAT IS LEFT OUT, and why each one's quiet is not a dropped ball:
 *   · `done` / `wontdo` — finished. There is nothing left to move; the quiet is
 *     the quiet of completed work.
 *   · `backlog` — the holding pen. Nothing here has been committed and no doer has
 *     picked it up, so a backlog card sitting still is a card still waiting to be
 *     CHOSEN, not one that has been forgotten mid-flight. Nudging it would nag
 *     about every idea anyone ever parked. (This is the "arguably backlog" call:
 *     we treat it as exempt.)
 */
const ACTIVE_LANES: ReadonlySet<IssueStatus> = new Set<IssueStatus>([
  'todo',
  'inprogress',
  'staging',
  'inreview',
]);

/**
 * THE SLICE OF A CARD THIS MODULE READS — a Pick of Issue, so a real Issue passes
 * straight in and a test fixture only has to carry these fields.
 *
 *   · `status` / `size` — which lane it is in, and which window scales it.
 *   · `standsLog` / `startedAt` / `updatedAt` — the movement signals (`lastMovementMs`).
 *   · `heldAt` — the explicit on-hold stamp (migration 0201).
 *   · `id` / `ref` / `blockerRefs` — the fields `startGate` needs to decide whether
 *     an unfinished predecessor is holding the card up (a real Issue satisfies
 *     `DepCard` structurally, which is why this card goes straight into `startGate`).
 */
export type IdleCard = Pick<
  Issue,
  'id' | 'ref' | 'status' | 'size' | 'updatedAt' | 'startedAt' | 'standsLog' | 'heldAt' | 'blockerRefs'
>;

/**
 * WHEN THE CARD LAST ACTUALLY MOVED — the most recent of three honest signals:
 *   (a) the newest entry in its status log (`standsLog[].at`) — a narration or a
 *       status move is unambiguous movement;
 *   (b) `startedAt` — the moment work began, for a card started but not yet narrated;
 *   (c) `updatedAt` — the last time anyone touched the card at all.
 *
 * WHY `updatedAt` IS IN HERE AT ALL. Movement, for this signal, is a STATUS change
 * or a PROGRESS-% change (the two things that say the work actually went somewhere).
 * A status change lands in `standsLog` as its own entry, so (a) catches it. A
 * progress-% change has NO dated event of its own in the history — the data model
 * records it only by bumping `updatedAt` — so `updatedAt` is the one signal that
 * reflects it, and it has to be read or a card nudged along purely by its percent
 * would look untouched.
 *
 * KNOWN v1 APPROXIMATION: `updatedAt` also bumps on edits that are NOT movement
 * (retitling, re-sizing, a tag change), so folding it in can read a card as "moved"
 * when it was only tidied. That is the error we choose: a false "not idle" costs a
 * nudge that should have fired; a false "idle" nags somebody who is working — and
 * nagging the working is how a team learns to ignore the signal. When a
 * movement-only timestamp for progress exists, narrow (c) to it.
 *
 * NOT `createdAt`. The question is when the card last MOVED, not when it was raised;
 * `createdAt` is deliberately not part of `IdleCard`, so it cannot leak in. All
 * three signals default to 0 when absent, so a fixture carrying none of them reads
 * as moved at the epoch, i.e. maximally idle.
 */
export function lastMovementMs(card: IdleCard): number {
  const log = card.standsLog ?? [];
  const newestLog = log.reduce(
    (max, e) => (e && typeof e.at === 'number' && e.at > max ? e.at : max),
    0,
  );
  return Math.max(newestLog, card.startedAt ?? 0, card.updatedAt ?? 0);
}

/**
 * WORKING DAYS BETWEEN TWO INSTANTS, Saturdays and Sundays excluded.
 *
 * Counts the weekdays STRICTLY AFTER `aMs`'s day up to and INCLUDING `bMs`'s day —
 * so Friday → Monday is ONE working day (Sat and Sun are skipped, Monday is the one
 * weekday that elapsed), never three. A span that does not move forward (`bMs` on
 * or before `aMs`'s day) is 0.
 *
 * WALKED A CALENDAR DAY AT A TIME via `Date.setDate`, not by adding 86.4M ms, for
 * the reason `addDays` in src/sprints.ts gives: a day that crosses a daylight-saving
 * change is 23 or 25 hours, and fixed-millisecond steps drift onto the wrong local
 * day across a long span. Days are compared at local midnight so the reader's own
 * week, not UTC's, decides what is a weekend.
 *
 * THE WEEKEND-ONLY CASE of {@link workingDaysBetween} (work-calendar.ts): identical
 * to `workingDaysBetween(aMs, bMs, DEFAULT_CALENDAR)`. Kept and pinned here for the
 * callers that are not calendar-aware; `idleVerdict`/`idleCards` take a calendar and
 * use the calendar-aware count, so a workspace's holidays and non-standard week are
 * honoured there without changing this one's meaning.
 */
export function businessDaysBetween(aMs: number, bMs: number): number {
  const dayStart = (ms: number) => {
    const d = new Date(ms);
    return new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  };
  const end = dayStart(bMs);
  const cur = new Date(dayStart(aMs));
  cur.setDate(cur.getDate() + 1); // start the morning AFTER the last movement
  let count = 0;
  while (cur.getTime() <= end) {
    const dow = cur.getDay(); // 0 Sun … 6 Sat
    if (dow !== 0 && dow !== 6) count += 1;
    cur.setDate(cur.getDate() + 1);
  }
  return count;
}

/**
 * THE VERDICT: has this card gone idle, and — when it has — since when.
 *
 * `since` is the millisecond of its last movement (`lastMovementMs`) when the card
 * is idle, so a caller can say "no movement for N working days" by running
 * `businessDaysBetween(since, now)`. It is `null` when the card is NOT idle,
 * whether because it is still inside its window or because it is exempt — in both
 * cases there is nothing to nudge, and a null keeps a caller from drawing a
 * "quiet since …" line where none belongs.
 */
export interface IdleVerdict {
  idle: boolean;
  since: number | null;
}

const NOT_IDLE: IdleVerdict = { idle: false, since: null };

/**
 * HAS THIS CARD GONE IDLE?
 *
 * The exemptions come first, because they are about whether the question even
 * applies, not about the arithmetic:
 *
 *   · NOT ACTIVE WORK. Only the lanes a doer is expected to be moving are nudged
 *     (`ACTIVE_LANES`): a finished card has nothing left to move, and a backlog
 *     card has no committed doer to nudge.
 *   · ON HOLD. `heldAt` is the explicit, deliberate pause (migration 0201); a held
 *     card is quiet BY DECISION, so it is never idle. Read defensively — the field
 *     is optional and null/absent both mean "running" — so a truthy stamp, and only
 *     a truthy stamp, exempts.
 *   · BLOCKED BY A STILL-OPEN PREDECESSOR. A card waiting on an unfinished card is
 *     the PREDECESSOR'S problem, not the doer's — nudging someone to "move it along"
 *     when they cannot is exactly the false alarm that trains people to ignore the
 *     signal. Decided by `startGate`, which reads the card-kind blocker refs and
 *     reports an open predecessor only while that predecessor is unresolved; the day
 *     the predecessor is done or won't-do, the card is judged on its own quiet
 *     again. (A blocker that names a person or free text, not a card, is NOT exempt
 *     here — this exemption is specifically the finish-to-start dependency case.)
 *
 * Otherwise it is working days since `lastMovementMs` against the size's window:
 * idle once the elapsed business days REACH the window (>=, so a 3-day window fires
 * on the third quiet day, not the fourth).
 *
 * `byRef` is the rest of the board keyed by card ref — the same map `startGate`
 * takes — used only to resolve those predecessors. A predecessor that is not in the
 * map cannot be judged and so does not exempt the card (startGate's own rule): we
 * do not hold a card quiet on evidence we were not handed.
 */
export function idleVerdict(
  card: IdleCard,
  byRef: Map<string, DepCard>,
  windows: IdleWindows,
  nowMs: number,
  cal: WorkCalendar = DEFAULT_CALENDAR,
): IdleVerdict {
  if (!ACTIVE_LANES.has(card.status)) return NOT_IDLE;
  if (card.heldAt != null) return NOT_IDLE; // truthy stamp = paused on purpose
  if (startGate(card, byRef).blocked) return NOT_IDLE;

  const since = lastMovementMs(card);
  // Working days elapsed, the workspace calendar's own (0216): its holidays and a
  // non-standard week don't count against a doer. Inert default → exactly weekends.
  const idle = workingDaysBetween(since, nowMs, cal) >= windowForSize(card.size, windows);
  return idle ? { idle: true, since } : NOT_IDLE;
}

/**
 * THE IDLE CARDS, MOST IDLE FIRST. Only the cards that are actually idle (the
 * exemptions above already dropped), ordered by working days quiet — so a caller
 * showing "the board's stalest work" leads with the card that has been quiet
 * longest, the one most likely to have been forgotten.
 *
 * The `byRef` map `startGate` needs is built ONCE here and shared across every
 * card, so a board-wide read is one pass over the cards, not one per card.
 */
export function idleCards(
  cards: IdleCard[],
  windows: IdleWindows,
  nowMs: number,
  cal: WorkCalendar = DEFAULT_CALENDAR,
): Array<{ card: IdleCard; since: number }> {
  const byRef = new Map<string, DepCard>(cards.map((c) => [c.ref, c] as [string, DepCard]));
  return cards
    .map((card) => ({ card, verdict: idleVerdict(card, byRef, windows, nowMs, cal) }))
    .filter((r): r is { card: IdleCard; verdict: { idle: true; since: number } } => r.verdict.idle)
    .map((r) => ({ card: r.card, since: r.verdict.since }))
    .sort((a, b) => workingDaysBetween(b.since, nowMs, cal) - workingDaysBetween(a.since, nowMs, cal));
}
